import React from 'react';
import { useTranslation } from 'react-i18next';

import { Root, Label, Control, Chevron, ErrorText } from './Select.styles';

export type SelectOption = {
  /** The stored value. */
  value: string;
  /** Human label — an i18n key or natural-language data (e.g. a country name). */
  label: string;
};

export type SelectProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Disabled first option shown while `value` is empty (e.g. "Select a country"). */
  placeholder?: string;
  /** Inline validation message rendered beneath the control; also reddens it. */
  error?: string;
  disabled?: boolean;
  /** Identifier surfaced via `event.currentTarget.dataset.id` if a caller needs it. */
  id?: string;
  /**
   * Hide the visible label while keeping it as the control's accessible name (the
   * `<select>` still carries `aria-label={label}`). Use when a caller renders its
   * own heading above the control (e.g. a Settings group's GroupLabel).
   */
  hideLabel?: boolean;
};

/**
 * Labelled dropdown built on a native `<select>` — accessible, keyboard-friendly,
 * and uses the OS picker on mobile — styled to match `Field`. The label and error
 * render via `<Text>` (translation-ready, auto-escaped); option text goes through
 * `t()` because a native `<option>` can only hold a string, not a React node (same
 * exception as attribute copy).
 */
export const Select = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled = false,
  id,
  hideLabel = false,
}: SelectProps): React.ReactElement => {
  const { t } = useTranslation();
  const invalid = Boolean(error);
  return (
    <Root>
      {hideLabel ? null : <Label text={label} tag="span" />}
      <Control $invalid={invalid} $placeholder={value === ''}>
        <select
          value={value}
          aria-label={t(label)}
          aria-invalid={invalid || undefined}
          data-id={id}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {placeholder !== undefined ? (
            <option value="" disabled>
              {t(placeholder)}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
        <Chevron viewBox="0 0 12 12" aria-hidden="true">
          <polyline points="2,4 6,8 10,4" strokeLinecap="round" strokeLinejoin="round" />
        </Chevron>
      </Control>
      {error ? <ErrorText text={error} tag="span" /> : null}
    </Root>
  );
};

export default Select;
